import React, { useContext, useState, useEffect } from 'react';
import AuthContext from '../../context/AuthContext';
import ThemeContext from '../../context/ThemeContext';
import axios from 'axios';
import {
    Box,
    Button,
    Container,
    Heading,
    Text,
    VStack,
    HStack,
    useToast,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    useDisclosure,
    Card,
    CardBody,
    Flex,
    Icon,
    Input,
    FormControl,
    FormLabel,
    FormErrorMessage,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    TableContainer,
    Badge,
    Spinner,
    InputGroup,
    InputLeftElement,
    Select,
} from '@chakra-ui/react';
import { FaUserPlus, FaUsers, FaEnvelope, FaLock, FaIdCard, FaCog } from 'react-icons/fa';

const isProduction = process.env.NODE_ENV === 'production';
const API_BASE_URL = isProduction
    ? process.env.REACT_APP_API_BASE_URL_PROD
    : process.env.REACT_APP_API_BASE_URL_DEV;

function SettingsEmployeeManagementPaid() {
    const { token } = useContext(AuthContext);
    const { themeData } = useContext(ThemeContext);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();

    // Extract theme colors with fallbacks
    const primaryColor = themeData?.colors?.primary || '#2B6CB0';
    const secondaryColor = themeData?.colors?.secondary || '#2C5282';
    const accentColor = themeData?.colors?.accent || '#63B3ED';

    // Determine if theme is dark (midnight)
    const isDarkTheme = themeData?.id === 'midnight';

    // Theme-aware colors
    const headingTextColor = isDarkTheme ? '#F7FAFC' : primaryColor;
    const bodyTextColor = isDarkTheme ? '#E2E8F0' : 'gray.600';
    const mutedTextColor = isDarkTheme ? '#CBD5E0' : 'gray.500';
    const labelTextColor = isDarkTheme ? '#E2E8F0' : 'gray.700';
    const strongTextColor = isDarkTheme ? '#F7FAFC' : 'gray.800';
    const cardBg = isDarkTheme ? 'rgba(45, 55, 72, 0.6)' : 'white';
    const headerBg = isDarkTheme ? 'rgba(66, 153, 225, 0.15)' : primaryColor;
    const infoBg = isDarkTheme ? 'rgba(66, 153, 225, 0.1)' : 'rgba(99, 179, 237, 0.1)';
    const rowHoverBg = isDarkTheme ? 'rgba(66, 153, 225, 0.08)' : 'gray.50';
    const inputIconColor = isDarkTheme ? '#CBD5E0' : 'gray.400';
    const borderColor = isDarkTheme ? 'rgba(66, 153, 225, 0.3)' : 'gray.200';
    const tableHeaderBg = isDarkTheme ? 'rgba(66, 153, 225, 0.1)' : 'gray.50';
    const buttonHoverBg = isDarkTheme ? accentColor : secondaryColor;
    const ghostHoverBg = isDarkTheme ? 'rgba(66, 153, 225, 0.15)' : 'gray.100';

    // Role options for the dropdown
    const roleOptions = [
        { value: 'rm', label: 'Relationship Manager (RM)', color: 'pink' },
        { value: 'compliance', label: 'Compliance', color: 'blue' },
        { value: 'head', label: 'Head', color: 'orange' },
        { value: 'view_only', label: 'View Only', color: 'green' },
        { value: 'portfolio_adviser', label: 'Portfolio Advisor', color: 'yellow' }
    ];

    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingEmployees, setLoadingEmployees] = useState(true);
    const [formData, setFormData] = useState({
        email: '',
        name: '',
        password: '',
        role: 'rm',
    });
    const [formErrors, setFormErrors] = useState({});
    const { isOpen: isResetOpen, onOpen: onResetOpen, onClose: onResetClose } = useDisclosure();
    const [resetEmployee, setResetEmployee] = useState(null);
    const [newPassword, setNewPassword] = useState('');
    const [resetLoading, setResetLoading] = useState(false);
    const [passwordError, setPasswordError] = useState('');

    useEffect(() => {
        fetchEmployees();
        // eslint-disable-next-line
    }, []);

    const fetchEmployees = async () => {
        setLoadingEmployees(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/roles/list-all-employees`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setEmployees(response.data.employees || []);
        } catch (error) {
            toast({
                title: 'Error',
                description: error.response?.data?.message || 'Failed to fetch employees',
                status: 'error',
                duration: 5000,
                isClosable: true,
                position: 'top',
            });
        } finally {
            setLoadingEmployees(false);
        }
    };

    const validateForm = () => {
        const errors = {};

        if (!formData.name.trim()) {
            errors.name = 'Name is required';
        }

        if (!formData.email.trim()) {
            errors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            errors.email = 'Invalid email format';
        }

        if (!formData.password) {
            errors.password = 'Password is required';
        } else if (formData.password.length < 6) {
            errors.password = 'Password must be at least 6 characters';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error for this field when user starts typing
        if (formErrors[name]) {
            setFormErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleCreateEmployee = async () => {
        if (!validateForm()) {
            return;
        }

        setLoading(true);
        try {
            const response = await axios.post(
                `${API_BASE_URL}/roles/create-new-employee`,
                {
                    email: formData.email,
                    name: formData.name,
                    password: formData.password,
                    role: formData.role,
                },
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );

            toast({
                title: 'Success!',
                description: response.data.message || 'Employee added successfully',
                status: 'success',
                duration: 5000,
                isClosable: true,
                position: 'top',
            });

            // Reset form and close modal
            setFormData({ email: '', name: '', password: '', role: 'rm' });
            setFormErrors({});
            onClose();

            // Refresh employee list
            fetchEmployees();
        } catch (error) {
            toast({
                title: 'Error',
                description: error.response?.data?.message || 'Failed to create employee',
                status: 'error',
                duration: 5000,
                isClosable: true,
                position: 'top',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteEmployee = async (email) => {
        if (!window.confirm(`Are you sure you want to delete ${email}?`)) return;

        try {
            const response = await axios.delete(`${API_BASE_URL}/roles/delete-employee`, {
                headers: { Authorization: `Bearer ${token}` },
                data: { email }, // 👈 DELETE requires 'data' field for body
            });

            toast({
                title: 'Success',
                description: response.data.message || 'Employee deleted successfully',
                status: 'success',
                duration: 5000,
                isClosable: true,
                position: 'top',
            });

            // Refresh employee list
            fetchEmployees();
        } catch (error) {
            toast({
                title: 'Error',
                description: error.response?.data?.message || 'Failed to delete employee',
                status: 'error',
                duration: 5000,
                isClosable: true,
                position: 'top',
            });
        }
    };

    const handleResetPassword = async () => {
        if (!newPassword) {
            setPasswordError('Password is required');
            return;
        }
        if (newPassword.length < 6) {
            setPasswordError('Password must be at least 6 characters');
            return;
        }

        setResetLoading(true);
        try {
            const response = await axios.post(
                `${API_BASE_URL}/auth/reset-rm-password`,
                {
                    email: resetEmployee.email,
                    newPassword: newPassword,
                },
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );

            toast({
                title: 'Success',
                description: response.data.message || 'Password reset successfully',
                status: 'success',
                duration: 5000,
                isClosable: true,
                position: 'top',
            });

            // Reset and close
            setNewPassword('');
            setPasswordError('');
            setResetEmployee(null);
            onResetClose();
        } catch (error) {
            toast({
                title: 'Error',
                description: error.response?.data?.message || 'Failed to reset password',
                status: 'error',
                duration: 5000,
                isClosable: true,
                position: 'top',
            });
        } finally {
            setResetLoading(false);
        }
    };

    const openResetModal = (employee) => {
        setResetEmployee(employee);
        setNewPassword('');
        setPasswordError('');
        onResetOpen();
    };

    const getRoleBadgeColor = (role) => {
        const colors = {
            admin: 'purple',
            rm: 'pink',
            compliance: 'blue',
            head: 'orange',
            view: 'green',
            view_only: 'green',
        };
        return colors[role] || 'gray';
    };

    const getSelectedRoleOption = () => {
        return roleOptions.find(r => r.value === formData.role) || roleOptions[0];
    };

    return (
        <Container maxW="container.xl" mt={4}>
            <VStack spacing={10} align="stretch">
                {/* Action Button Section */}
                <Card
                    borderRadius="xl"
                    boxShadow="md"
                    bg={cardBg}
                    borderWidth="1px"
                    borderColor={borderColor}
                >
                    <CardBody>
                        <Flex
                            justify="space-between"
                            align="center"
                            flexWrap="wrap"
                            gap={4}
                        >
                            <HStack spacing={4}>
                                <Icon
                                    as={FaUsers}
                                    boxSize={8}
                                    color={isDarkTheme ? accentColor : primaryColor}
                                />
                                <Box>
                                    <Text fontSize="sm" color={mutedTextColor}>
                                        Total Employees
                                    </Text>
                                    <Heading
                                        size="lg"
                                        color={isDarkTheme ? accentColor : primaryColor}
                                    >
                                        {employees.length}
                                    </Heading>
                                </Box>
                            </HStack>

                            <Button
                                leftIcon={<FaUserPlus />}
                                onClick={onOpen}
                                bg={primaryColor}
                                color="white"
                                size="lg"
                                _hover={{ bg: buttonHoverBg }}
                            >
                                Add Employee
                            </Button>
                        </Flex>
                    </CardBody>
                </Card>

                {/* Employee List */}
                <Card
                    borderRadius="xl"
                    boxShadow="md"
                    overflow="hidden"
                    bg={cardBg}
                    borderWidth="1px"
                    borderColor={borderColor}
                >
                    <CardBody p={0}>
                        <Box
                            bg={headerBg}
                            color={isDarkTheme ? headingTextColor : 'white'}
                            p={6}
                            borderBottom="1px"
                            borderColor={borderColor}
                        >
                            <Heading size="md">
                                <HStack spacing={2}>
                                    <Icon as={FaUsers} />
                                    <Text>Role Directory</Text>
                                </HStack>
                            </Heading>
                        </Box>

                        <Box p={6}>
                            {loadingEmployees ? (
                                <Flex justify="center" align="center" py={10}>
                                    <Spinner
                                        thickness="4px"
                                        speed="0.65s"
                                        emptyColor={borderColor}
                                        color={isDarkTheme ? accentColor : primaryColor}
                                        size="xl"
                                    />
                                </Flex>
                            ) : employees.length === 0 ? (
                                <Box textAlign="center" py={10}>
                                    <Icon
                                        as={FaUsers}
                                        boxSize={16}
                                        color={mutedTextColor}
                                        mb={4}
                                    />
                                    <Text color={bodyTextColor} fontSize="lg" fontWeight="medium">
                                        No personnel found
                                    </Text>
                                    <Text color={mutedTextColor} fontSize="sm" mt={2}>
                                        Click "Add Employee" to create your first employee/personnel
                                    </Text>
                                </Box>
                            ) : (
                                <TableContainer>
                                    <Table variant="simple">
                                        <Thead bg={tableHeaderBg}>
                                            <Tr>
                                                <Th
                                                    fontSize="sm"
                                                    textTransform="uppercase"
                                                    color={labelTextColor}
                                                    fontWeight="bold"
                                                    borderColor={borderColor}
                                                >
                                                    Name
                                                </Th>
                                                <Th
                                                    fontSize="sm"
                                                    textTransform="uppercase"
                                                    color={labelTextColor}
                                                    fontWeight="bold"
                                                    borderColor={borderColor}
                                                >
                                                    Email
                                                </Th>
                                                <Th
                                                    fontSize="sm"
                                                    textTransform="uppercase"
                                                    color={labelTextColor}
                                                    fontWeight="bold"
                                                    borderColor={borderColor}
                                                >
                                                    Role
                                                </Th>
                                                <Th
                                                    fontSize="sm"
                                                    textTransform="uppercase"
                                                    color={labelTextColor}
                                                    fontWeight="bold"
                                                    borderColor={borderColor}
                                                >
                                                    Action
                                                </Th>
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            {employees.map((employee, index) => (
                                                <Tr
                                                    key={index}
                                                    _hover={{ bg: rowHoverBg }}
                                                >
                                                    <Td
                                                        fontWeight="medium"
                                                        color={strongTextColor}
                                                        borderColor={borderColor}
                                                    >
                                                        {employee.name}
                                                    </Td>
                                                    <Td
                                                        color={bodyTextColor}
                                                        borderColor={borderColor}
                                                    >
                                                        {employee.email}
                                                    </Td>
                                                    <Td borderColor={borderColor}>
                                                        <Badge
                                                            colorScheme={getRoleBadgeColor(employee.role)}
                                                            fontSize="sm"
                                                            px={3}
                                                            py={1}
                                                            borderRadius="full"
                                                            textTransform="uppercase"
                                                        >
                                                            {employee.role}
                                                        </Badge>
                                                    </Td>
                                                    <Td borderColor={borderColor}>
                                                        <HStack spacing={2}>
                                                            <Button
                                                                bg={primaryColor}
                                                                color="white"
                                                                size="sm"
                                                                leftIcon={<FaCog />}
                                                                onClick={() => openResetModal(employee)}
                                                                _hover={{ bg: buttonHoverBg }}
                                                            >
                                                                Settings
                                                            </Button>
                                                            <Button
                                                                colorScheme="red"
                                                                size="sm"
                                                                variant="outline"
                                                                onClick={() => handleDeleteEmployee(employee.email)}
                                                            >
                                                                Delete
                                                            </Button>
                                                        </HStack>
                                                    </Td>
                                                </Tr>
                                            ))}
                                        </Tbody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Box>
                    </CardBody>
                </Card>
            </VStack>

            {/* Create Employee Modal */}
            <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
                <ModalOverlay
                    backdropFilter="blur(6px)"
                    bg={isDarkTheme ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.4)'}
                />
                <ModalContent
                    borderRadius="xl"
                    bg={cardBg}
                    borderWidth="1px"
                    borderColor={borderColor}
                >
                    <ModalHeader>
                        <HStack spacing={2}>
                            <Icon
                                as={FaUserPlus}
                                color={isDarkTheme ? accentColor : primaryColor}
                            />
                            <Text color={headingTextColor}>Add New Employee</Text>
                        </HStack>
                    </ModalHeader>
                    <ModalCloseButton color={bodyTextColor} />
                    <ModalBody pb={6}>
                        <VStack spacing={5} align="stretch">
                            <FormControl isInvalid={formErrors.name}>
                                <FormLabel fontWeight="semibold" color={labelTextColor}>
                                    Full Name
                                </FormLabel>
                                <InputGroup>
                                    <InputLeftElement pointerEvents="none">
                                        <Icon as={FaIdCard} color={inputIconColor} />
                                    </InputLeftElement>
                                    <Input
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        placeholder="Enter full name"
                                        size="lg"
                                        focusBorderColor={isDarkTheme ? accentColor : primaryColor}
                                        bg={isDarkTheme ? 'rgba(45, 55, 72, 0.4)' : 'white'}
                                        color={strongTextColor}
                                        borderColor={borderColor}
                                        _placeholder={{ color: mutedTextColor }}
                                    />
                                </InputGroup>
                                <FormErrorMessage>{formErrors.name}</FormErrorMessage>
                            </FormControl>

                            <FormControl isInvalid={formErrors.email}>
                                <FormLabel fontWeight="semibold" color={labelTextColor}>
                                    Email Address
                                </FormLabel>
                                <InputGroup>
                                    <InputLeftElement pointerEvents="none">
                                        <Icon as={FaEnvelope} color={inputIconColor} />
                                    </InputLeftElement>
                                    <Input
                                        name="email"
                                        type="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        placeholder="Enter email address"
                                        size="lg"
                                        focusBorderColor={isDarkTheme ? accentColor : primaryColor}
                                        bg={isDarkTheme ? 'rgba(45, 55, 72, 0.4)' : 'white'}
                                        color={strongTextColor}
                                        borderColor={borderColor}
                                        _placeholder={{ color: mutedTextColor }}
                                    />
                                </InputGroup>
                                <FormErrorMessage>{formErrors.email}</FormErrorMessage>
                            </FormControl>

                            <FormControl isInvalid={formErrors.password}>
                                <FormLabel fontWeight="semibold" color={labelTextColor}>
                                    Password
                                </FormLabel>
                                <InputGroup>
                                    <InputLeftElement pointerEvents="none">
                                        <Icon as={FaLock} color={inputIconColor} />
                                    </InputLeftElement>
                                    <Input
                                        name="password"
                                        type="password"
                                        value={formData.password}
                                        onChange={handleInputChange}
                                        placeholder="Enter password"
                                        size="lg"
                                        focusBorderColor={isDarkTheme ? accentColor : primaryColor}
                                        bg={isDarkTheme ? 'rgba(45, 55, 72, 0.4)' : 'white'}
                                        color={strongTextColor}
                                        borderColor={borderColor}
                                        _placeholder={{ color: mutedTextColor }}
                                    />
                                </InputGroup>
                                <FormErrorMessage>{formErrors.password}</FormErrorMessage>
                            </FormControl>

                            <FormControl>
                                <FormLabel fontWeight="semibold" color={labelTextColor}>
                                    Role Assignment
                                </FormLabel>
                                <Select
                                    name="role"
                                    value={formData.role}
                                    onChange={handleInputChange}
                                    size="lg"
                                    focusBorderColor={isDarkTheme ? accentColor : primaryColor}
                                    bg={isDarkTheme ? 'rgba(45, 55, 72, 0.4)' : 'white'}
                                    color={strongTextColor}
                                    borderColor={borderColor}
                                >
                                    {roleOptions.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </Select>
                            </FormControl>

                            <Box
                                p={4}
                                bg={infoBg}
                                borderRadius="lg"
                                borderLeft="4px solid"
                                borderLeftColor={isDarkTheme ? accentColor : primaryColor}
                                borderWidth="1px"
                                borderColor={borderColor}
                            >
                                <Text fontSize="sm" color={bodyTextColor} mb={2}>
                                    Selected Role
                                </Text>
                                <Badge
                                    colorScheme={getSelectedRoleOption().color}
                                    fontSize="md"
                                    px={3}
                                    py={1}
                                    borderRadius="full"
                                    textTransform="uppercase"
                                >
                                    {getSelectedRoleOption().label}
                                </Badge>
                            </Box>
                        </VStack>
                    </ModalBody>

                    <ModalFooter>
                        <Button
                            variant="ghost"
                            mr={3}
                            onClick={() => {
                                onClose();
                                setFormData({ email: '', name: '', password: '', role: 'rm' });
                                setFormErrors({});
                            }}
                            color={bodyTextColor}
                            _hover={{ bg: ghostHoverBg }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleCreateEmployee}
                            isLoading={loading}
                            loadingText="Creating..."
                            bg={primaryColor}
                            color="white"
                            _hover={{ bg: buttonHoverBg }}
                        >
                            Create Employee
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Reset Password Modal */}
            <Modal isOpen={isResetOpen} onClose={() => {
                onResetClose();
                setNewPassword('');
                setPasswordError('');
                setResetEmployee(null);
            }} isCentered size="md">
                <ModalOverlay
                    backdropFilter="blur(6px)"
                    bg={isDarkTheme ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.4)'}
                />
                <ModalContent
                    borderRadius="xl"
                    bg={cardBg}
                    borderWidth="1px"
                    borderColor={borderColor}
                >
                    <ModalHeader>
                        <HStack spacing={2}>
                            <Icon
                                as={FaCog}
                                color={isDarkTheme ? accentColor : primaryColor}
                            />
                            <Text color={headingTextColor}>Reset Password</Text>
                        </HStack>
                    </ModalHeader>
                    <ModalCloseButton color={bodyTextColor} />
                    <ModalBody pb={6}>
                        {resetEmployee && (
                            <VStack spacing={4} align="stretch">
                                <Box
                                    p={3}
                                    bg={infoBg}
                                    borderRadius="md"
                                    borderLeft="3px solid"
                                    borderLeftColor={isDarkTheme ? accentColor : primaryColor}
                                    borderWidth="1px"
                                    borderColor={borderColor}
                                >
                                    <Text fontSize="sm" color={bodyTextColor} mb={1}>
                                        Employee
                                    </Text>
                                    <Text fontWeight="semibold" fontSize="md" color={strongTextColor}>
                                        {resetEmployee.name}
                                    </Text>
                                    <Text fontSize="sm" color={mutedTextColor}>
                                        {resetEmployee.email}
                                    </Text>
                                </Box>

                                <FormControl isInvalid={passwordError}>
                                    <FormLabel fontWeight="semibold" color={labelTextColor}>
                                        New Password
                                    </FormLabel>
                                    <InputGroup>
                                        <InputLeftElement pointerEvents="none">
                                            <Icon as={FaLock} color={inputIconColor} />
                                        </InputLeftElement>
                                        <Input
                                            type="password"
                                            value={newPassword}
                                            onChange={(e) => {
                                                setNewPassword(e.target.value);
                                                setPasswordError('');
                                            }}
                                            placeholder="Enter new password"
                                            size="lg"
                                            focusBorderColor={isDarkTheme ? accentColor : primaryColor}
                                            bg={isDarkTheme ? 'rgba(45, 55, 72, 0.4)' : 'white'}
                                            color={strongTextColor}
                                            borderColor={borderColor}
                                            _placeholder={{ color: mutedTextColor }}
                                        />
                                    </InputGroup>
                                    <FormErrorMessage>{passwordError}</FormErrorMessage>
                                </FormControl>
                            </VStack>
                        )}
                    </ModalBody>

                    <ModalFooter>
                        <Button
                            variant="ghost"
                            mr={3}
                            onClick={() => {
                                onResetClose();
                                setNewPassword('');
                                setPasswordError('');
                                setResetEmployee(null);
                            }}
                            color={bodyTextColor}
                            _hover={{ bg: ghostHoverBg }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleResetPassword}
                            isLoading={resetLoading}
                            loadingText="Resetting..."
                            bg={primaryColor}
                            color="white"
                            _hover={{ bg: buttonHoverBg }}
                        >
                            Reset Password
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Container>
    );
}

export default SettingsEmployeeManagementPaid;